import React from 'react';

export const actionTypes = {
  ALL_SECTIONS_TOGGLED: 'ALL_SECTIONS_TOGGLED',
  CHANGES_SAVED: 'CHANGES_SAVED',
  DIRTY_UPDATED: 'DIRTY_UPDATED',
  ORIGINAL_DATA_LOADED: 'ORIGINAL_DATA_LOADED',
  RESET_DIRTY: 'RESET_DIRTY',
  SECTION_OPENED: 'SECTION_OPENED',
  SNACKBAR_SET: 'SNACKBAR_SET',
  SWITCH_TOGGLED: 'SWITCH_TOGGLED',
  TOGGLE_FAILED: 'TOGGLE_FAILED',
  TOGGLE_DRAWER: 'TOGGLE_DRAWER',
  UNANSWERED_CHANGES_SAVED: 'UNANSWERED_CHANGES_SAVED',
};

export const initialState = {
  loading: true,
  updated: false,
  dirtySections: {
    always: false,
    selective: false,
    unanswered: false,
    busy: false,
  },
  openSections: {
    always: false,
    selective: false,
    unanswered: false,
    busy: false,
  },
  originalFields: {
    always: {},
    selective: {},
    unanswered: {},
    busy: {},
  },
  originalEnabled: {
    always: false,
    selective: false,
    unanswered: false,
    busy: false,
  },
  snackbar: {
    message: '',
    type: '',
  },
  drawer: {
    open: false,
    type: '', // string: 'Add' or 'Update'
    updateIndex: null,
  },
};

export const callForwardingReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case actionTypes.ALL_SECTIONS_TOGGLED: {
      const newSections = { ...state.openSections };
      newSections.always = payload;
      newSections.selective = payload;
      newSections.busy = payload;
      newSections.unanswered = payload;

      return { ...state, openSections: newSections };
    }

    case actionTypes.CHANGES_SAVED: {
      const newEnabled = {
        always: Boolean(payload.always.enabled),
        selective: Boolean(payload.selective.enabled),
        busy: Boolean(payload.busy.enabled),
        unanswered: !!payload.unanswered.forwardToPhoneNumber,
      };

      const newFields = {
        always: { phoneNumber: payload.always.phoneNumber || '' },
        busy: { phoneNumber: payload.busy.phoneNumber || '' },
        unanswered: {
          forwardToVoicemail: payload.unanswered.forwardToVoicemail,
          forwardToPhoneNumber: payload.unanswered.forwardToPhoneNumber || '',
          forwardTimeoutSeconds: payload.unanswered.forwardTimeoutSeconds || 0,
        },
        selective: {
          availableSchedules: payload.selective.availableSchedules || [],
          conditionList: payload.selective.conditionList || [],
          enabled: payload.selective.enabled || false,
          phoneNumber: payload.selective.phoneNumber || '',
          playRingBack: payload.selective.playRingBack || false,
        },
      };

      return {
        ...state,
        updated: true,
        originalEnabled: newEnabled,
        originalFields: newFields,
      };
    }

    case actionTypes.UNANSWERED_CHANGES_SAVED: {
      const unansweredEnabled = !!payload.forwardToPhoneNumber;

      const newUnanswered = {
        forwardToVoicemail: payload.forwardToVoicemail,
        forwardToPhoneNumber: payload.forwardToPhoneNumber || '',
        forwardTimeoutSeconds: payload.forwardTimeoutSeconds || 0,
      };

      return {
        ...state,
        updated: true,
        originalEnabled: {
          ...state.originalEnabled,
          unanswered: unansweredEnabled,
        },
        originalFields: { ...state.originalFields, unanswered: newUnanswered },
      };
    }

    case actionTypes.DIRTY_UPDATED: {
      const newDirty = { ...state.dirtySections };
      newDirty[payload.sectionName] = payload.dirty;

      return { ...state, dirtySections: newDirty };
    }

    case actionTypes.ORIGINAL_DATA_LOADED: {
      const enabled = {
        always: Boolean(payload.always.enabled),
        selective: Boolean(payload.selective.enabled),
        busy: Boolean(payload.busy.enabled),
        unanswered: !!payload.unanswered.forwardToPhoneNumber,
      };

      const fields = {
        always: { phoneNumber: payload.always.phoneNumber || '' },
        busy: { phoneNumber: payload.busy.phoneNumber || '' },
        unanswered: {
          forwardToVoicemail: payload.unanswered.forwardToVoicemail,
          forwardToPhoneNumber: payload.unanswered.forwardToPhoneNumber || '',
          forwardTimeoutSeconds: payload.unanswered.forwardTimeoutSeconds || 0,
        },
        selective: {
          availableSchedules: payload.selective.availableSchedules || [],
          conditionList: payload.selective.conditionList || [],
          phoneNumber: payload.selective.phoneNumber || '',
          playRingBack: Boolean(payload.selective?.playRingBack),
        },
      };

      return {
        ...state,
        originalEnabled: enabled,
        originalFields: fields,
        loading: false,
      };
    }

    case actionTypes.RESET_DIRTY: {
      const dirtySections = {
        always: false,
        selective: false,
        unanswered: false,
        busy: false,
      };
      return { ...state, dirtySections };
    }

    case actionTypes.SECTION_VISIBILITY_TOGGLED: {
      const newSections = { ...state.openSections };
      newSections[payload] = !state.openSections[payload];

      return { ...state, openSections: newSections };
    }

    case actionTypes.SNACKBAR_SET: {
      return { ...state, snackbar: { ...payload } };
    }

    case actionTypes.SWITCH_TOGGLED: {
      return {
        ...state,
        updated: true,
        originalEnabled: {
          ...state.originalEnabled,
          [payload.sectionName]: payload.enabled,
        },
      };
    }

    case actionTypes.TOGGLE_FAILED: {
      const newSections = { ...state.openSections };
      newSections[payload.section] = true;

      return {
        ...state,
        openSections: newSections,
        snackbar: {
          message: payload.message,
          type: payload.type,
        },
      };
    }

    case actionTypes.TOGGLE_DRAWER: {
      return {
        ...state,
        drawer: action.payload,
      };
    }

    default: {
      throw new Error(`unsupported type: ${type}`);
    }
  }
};

export const CallForwardContext = React.createContext(initialState);
CallForwardContext.displayName = 'CallForwardContext';
